import { useEffect, useState } from 'react';
import { Checkbox, InputNumber, Modal, Select, TimePicker, message } from 'antd';
import dayjs from 'dayjs';
import { ActivityCategoryInfo, ActivityMatchInfo, ActivityScheduleConfig } from '../../interface';
import {
    SCHEDULE_BASE_HOUR,
    autoAlignSchedule,
    scheduleCourtId,
    scheduleTimeToMinutes,
    type ScheduleBoardEvent,
    type ScheduleTemplate,
} from './scheduleUtils';

interface ScheduleAutoAlignModalProps {
    visible: boolean;
    onClose: () => void;
    onSuccess: () => void;
    activity: ActivityMatchInfo;
    categories: ActivityCategoryInfo[] | null;
    templates: ScheduleTemplate[];
    originScheduleItems: ActivityScheduleConfig[];
    onPlaceEvents: (events: ScheduleBoardEvent[]) => void;
    endMinutes: number;
    currentDate: string;
    validStartTime: number;
}

const ScheduleAutoAlignModal: React.FC<ScheduleAutoAlignModalProps> = ({
    visible,
    onClose,
    onSuccess,
    activity,
    categories,
    templates,
    originScheduleItems,
    onPlaceEvents,
    endMinutes,
    currentDate,
    validStartTime,
}) => {
    const [messageApi, contextHolder] = message.useMessage();
    const [selectedStartTime, setSelectedStartTime] = useState<dayjs.Dayjs | null>(null);
    const [selectedCourts, setSelectedCourts] = useState<string[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
    const [selectedMatches, setSelectedMatches] = useState<number[]>([]);
    const [gapTime, setGapTime] = useState<number>(30);

    useEffect(() => {
        if (visible) {
            setSelectedCourts(Array.from({ length: activity.max_court_count }, (_, i) => scheduleCourtId(i + 1)));
            setSelectedCategory(null);
            setSelectedMatches([]);
        }
    }, [visible, activity.max_court_count]);

    const getAvailableMatches = () => {
        if (!selectedCategory) return [];
        return templates.filter(t => !t.used && originScheduleItems[t.id].category_id === selectedCategory);
    };

    const handleSelectAllMatches = (checked: boolean) => {
        const availableMatches = getAvailableMatches();
        setSelectedMatches(checked ? availableMatches.map(m => m.id) : []);
    };

    const handleConfirm = () => {
        if (!selectedStartTime || selectedCourts.length === 0 || selectedMatches.length === 0 || !selectedCategory) {
            messageApi.error('Please fill in all required fields');
            return;
        }

        const result = autoAlignSchedule({
            items: originScheduleItems,
            matchIds: getAvailableMatches().filter(t => selectedMatches.includes(t.id)).map(t => t.id),
            courtIds: selectedCourts,
            startMinutes: scheduleTimeToMinutes(selectedStartTime.format('HH:mm')),
            endMinutes,
            gapMinutes: gapTime,
        });

        if (!result.ok) {
            messageApi.error(result.error);
            return;
        }

        onPlaceEvents(result.placements);
        messageApi.success('Auto align completed successfully');
        onSuccess();
    };

    const disabledTime = () => {
        const validStartHour = Math.floor((SCHEDULE_BASE_HOUR * 60 + validStartTime) / 60);
        const validStartMinute = validStartTime % 60;

        return {
            disabledHours: () => Array.from({ length: validStartHour }, (_, i) => i),
            disabledMinutes: (selectedHour: number) => {
                if (selectedHour === validStartHour) {
                    return Array.from({ length: validStartMinute }, (_, i) => i);
                }
                return [];
            },
        };
    };

    return (
        <>
            {contextHolder}
            <Modal
                title={
                    <div>
                        <div>Quick Schedule Matches</div>
                        <div style={{
                            fontSize: '14px',
                            color: '#666',
                            marginTop: '4px',
                            fontWeight: 'normal',
                        }}>
                            Date: {currentDate}
                        </div>
                    </div>
                }
                open={visible}
                onOk={handleConfirm}
                onCancel={onClose}
                width={500}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div>
                        <div style={{ marginBottom: '8px', color: '#666' }}>Start Time</div>
                        <TimePicker
                            format="HH:mm"
                            minuteStep={10}
                            value={selectedStartTime}
                            needConfirm={false}
                            onChange={setSelectedStartTime}
                            style={{ width: '100%' }}
                            disabledTime={disabledTime}
                            showNow={false}
                        />
                    </div>
                    <div>
                        <div style={{ marginBottom: '8px', color: '#666' }}>Courts</div>
                        <Select
                            mode="multiple"
                            style={{ width: '100%' }}
                            value={selectedCourts}
                            onChange={setSelectedCourts}
                        >
                            {Array.from({ length: activity.max_court_count }, (_, i) => (
                                <Select.Option key={scheduleCourtId(i + 1)} value={scheduleCourtId(i + 1)}>
                                    COURT {i + 1}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                    <div>
                        <div style={{ marginBottom: '8px', color: '#666' }}>Category</div>
                        <Select
                            style={{ width: '100%' }}
                            value={selectedCategory}
                            onChange={(value) => {
                                setSelectedCategory(value);
                                setSelectedMatches([]);
                            }}
                            placeholder="Please select a category"
                        >
                            {categories?.map((category) => (
                                <Select.Option key={category.id} value={category.id}>
                                    {category.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                    <div>
                        <div style={{
                            marginBottom: '8px',
                            color: '#666',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}>
                            <span>Available Matches</span>
                            {selectedCategory && (
                                <Checkbox
                                    onChange={(e) => handleSelectAllMatches(e.target.checked)}
                                    checked={selectedMatches.length === getAvailableMatches().length && getAvailableMatches().length > 0}
                                >
                                    Select All
                                </Checkbox>
                            )}
                        </div>
                        <div style={{
                            maxHeight: '200px',
                            overflowY: 'auto',
                            border: '1px solid #d9d9d9',
                            borderRadius: '6px',
                            padding: '8px',
                        }}>
                            {selectedCategory ? (
                                getAvailableMatches().map(match => (
                                    <div key={match.id} style={{ marginBottom: '8px' }}>
                                        <Checkbox
                                            checked={selectedMatches.includes(match.id)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedMatches([...selectedMatches, match.id]);
                                                } else {
                                                    setSelectedMatches(selectedMatches.filter(id => id !== match.id));
                                                }
                                            }}
                                        >
                                            {match.title}
                                        </Checkbox>
                                    </div>
                                ))
                            ) : (
                                <div style={{ color: '#999', textAlign: 'center', padding: '20px' }}>
                                    Please select a category first
                                </div>
                            )}
                        </div>
                    </div>
                    <div>
                        <div style={{ marginBottom: '8px', color: '#666' }}>Gap Time (minutes)</div>
                        <InputNumber
                            min={0}
                            max={120}
                            value={gapTime}
                            onChange={value => setGapTime(value ?? 30)}
                            style={{ width: '100%' }}
                        />
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default ScheduleAutoAlignModal;
